import {
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  ElementNode,
  LexicalNode,
  TextNode,
} from 'lexical';
import { $isListItemNode, $isListNode } from '@lexical/list';
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  Transformer,
} from '@lexical/markdown';
import {
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableRowNode,
} from '@lexical/table';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { TABLE_ROW_REG_EXP, TABLE_ROW_DIVIDER_REG_EXP } from './config/transformers/table';
import type { TableSelectionInfo } from './types';
import {
  $applySuggestionMarks,
  $clearDeletionMarkFormatting,
  $removeAllSentinels,
  $stripOrphanedMarkersFromTree,
  markOrphanParents,
  normalizeInlineSuggestions,
  replaceSuggestionTagsWithSentinels,
  stripDeletionFormatting,
  stripFormattedSpaces,
} from './utils/suggestion-pipeline';
import { $dismissSuggestion, $isSuggestionNode, SuggestionNode } from './nodes/SuggestionNode';
import {
  $dismissSuggestionMark,
  $isSuggestionMarkNode,
  SuggestionMarkNode,
} from './nodes/SuggestionMarkNode';
import { isHtmlOrigin } from './utils/tableOrigin';

export function $containingSuggestionOrMark(
  node: LexicalNode | null | undefined,
): SuggestionNode | SuggestionMarkNode | null {
  let current = node;
  while (current) {
    if ($isSuggestionNode(current)) return current;
    if ($isSuggestionMarkNode(current)) return current;
    current = current.getParent();
  }
  return null;
}

const PLACEHOLDER_PATTERN = /\[(\{ADVISOR_TO_COMPLETE\})]\{\.Placeholder\}/g;
const PLACEHOLDER_REPLACEMENT = '<suggestion source="placeholder">$1</suggestion>';

export function convertPlaceholdersToSuggestionTags(md: string): string {
  return md.replace(PLACEHOLDER_PATTERN, PLACEHOLDER_REPLACEMENT);
}

export const replacePlaceholdersWithImages = (md: string, imgs: MarkdownImage[]) => {
  return imgs.reduce(
    (acc, img) => acc.replaceAll(`{image:${img.id}}`, `![Image](${img.encodedImage})`),
    md,
  );
};

export const replaceImagesWithPlaceholders = (md: string, imgs: MarkdownImage[]) => {
  return imgs.reduce(
    (acc, img) => acc.replaceAll(`![Image](${img.encodedImage})`, `{image:${img.id}}`),
    md,
  );
};

const NOTE_TAG_RE = /<suggestion[^>]*\bsource="note"[^>]*>([\s\S]*?)<\/suggestion>/g;

export function stripNoteSuggestionTags(md: string): string {
  return md.replace(NOTE_TAG_RE, '$1');
}

export function $setEditorValue(markdown: string, transformers: Transformer[]): void {
  const normalizedNewlines = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const normalized = normalizeInlineSuggestions(normalizedNewlines);
  const marked = markOrphanParents(normalized);
  const cleaned = stripFormattedSpaces(marked);
  const { processed: rawProcessed, spans } = replaceSuggestionTagsWithSentinels(cleaned);
  const processed = stripDeletionFormatting(rawProcessed, spans);

  const root = $getRoot();
  root.clear();
  $convertFromMarkdownString(processed, transformers, undefined, true);

  $applySuggestionMarks(spans);
  $removeAllSentinels();
  $clearDeletionMarkFormatting();
  $stripOrphanedMarkersFromTree();
}

export function $getEditorValue(transformers: Transformer[], node?: ElementNode): string {
  return $convertToMarkdownString(transformers, node, true);
}

const OPENING_TAG = '<replacement>';
const CLOSING_TAG = '</replacement>';
export function snapToWordBoundary(
  text: string,
  offset: number,
  direction: 'left' | 'right',
): number {
  if (direction === 'left') {
    if (offset <= 0) return offset;
    if (offset < text.length && /\s/.test(text[offset])) return offset;
    if (/\s/.test(text[offset - 1])) return offset;
    while (offset > 0 && !isWordBoundary(text, offset - 1, 'left')) offset--;
    return offset;
  } else {
    if (offset >= text.length) return offset;
    if (/\s/.test(text[offset])) return offset;
    if (offset > 0 && /\s/.test(text[offset - 1])) return offset;
    while (offset < text.length && !isWordBoundary(text, offset, 'right')) offset++;
    return offset;
  }
}

function isWordBoundary(text: string, index: number, direction: 'left' | 'right'): boolean {
  const ch = text[index];
  if (/\s/.test(ch)) return true;
  if (/[a-zA-Z0-9]/.test(ch)) return false;
  if (direction === 'left') {
    return index === 0 || /\s/.test(text[index - 1]);
  }
  return index === text.length - 1 || /\s/.test(text[index + 1]);
}

type InsertedMarkers = {
  openingTagNode: TextNode;
  closingTagNode: TextNode;
};

export function $insertReplacementMarkers(): InsertedMarkers | null {
  let selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return null;
  }

  const overlappingSuggestions = new Map<string, SuggestionNode | SuggestionMarkNode>();
  for (const node of selection.getNodes()) {
    const suggestion = $containingSuggestionOrMark(node);
    if (suggestion && !overlappingSuggestions.has(suggestion.getKey())) {
      overlappingSuggestions.set(suggestion.getKey(), suggestion);
    }
  }
  for (const suggestion of overlappingSuggestions.values()) {
    if ($isSuggestionNode(suggestion)) {
      $dismissSuggestion(suggestion);
    } else if ($isSuggestionMarkNode(suggestion)) {
      $dismissSuggestionMark(suggestion);
    }
  }
  if (overlappingSuggestions.size > 0) {
    selection = $getSelection();
    if (!$isRangeSelection(selection) || selection.isCollapsed()) return null;
  }

  const startPoint = selection.isBackward() ? selection.focus : selection.anchor;
  const endPoint = selection.isBackward() ? selection.anchor : selection.focus;

  const selectedNodes = selection.getNodes();
  if (selectedNodes.length === 0) return null;

  const firstTextNode = selectedNodes.find((n): n is TextNode => $isTextNode(n));
  const lastTextNode = [...selectedNodes].reverse().find((n): n is TextNode => $isTextNode(n));

  if (!firstTextNode || !lastTextNode) return null;

  const startOffset =
    firstTextNode.getKey() === startPoint.getNode().getKey() ? startPoint.offset : 0;
  const endOffset =
    lastTextNode.getKey() === endPoint.getNode().getKey()
      ? endPoint.offset
      : lastTextNode.getTextContentSize();

  const openingTagNode = $createTextNode(OPENING_TAG);
  const closingTagNode = $createTextNode(CLOSING_TAG);

  if (firstTextNode === lastTextNode) {
    const snappedStart = snapToWordBoundary(firstTextNode.getTextContent(), startOffset, 'left');
    $insertTagAtWordBoundary(firstTextNode, startOffset, openingTagNode, 'before');

    const afterTag = openingTagNode.getNextSibling();
    if ($isTextNode(afterTag)) {
      const adjustedEndOffset = endOffset - snappedStart;
      $insertTagAtWordBoundary(afterTag, adjustedEndOffset, closingTagNode, 'after');
    }
  } else {
    $insertTagAtWordBoundary(firstTextNode, startOffset, openingTagNode, 'before');
    $insertTagAtWordBoundary(lastTextNode, endOffset, closingTagNode, 'after');
  }

  return { openingTagNode, closingTagNode };
}

export function $removeReplacementMarkers(markers: InsertedMarkers): void {
  markers.openingTagNode.remove();
  markers.closingTagNode.remove();
}

const BLOCK_PREFIX_BEFORE_REPLACEMENT =
  /^([ \t]*(?:[-*+]\s+|\d+\.\s+|#{1,6}\s+|>\s+))<replacement>/gm;

export function hoistReplacementTags(markdown: string): string {
  return markdown.replace(BLOCK_PREFIX_BEFORE_REPLACEMENT, '<replacement>$1');
}

export function $removeEmptyTableRows(): void {
  const root = $getRoot();
  for (const child of root.getChildren()) {
    if (!$isTableNode(child)) continue;

    const rows = child.getChildren().filter($isTableRowNode);
    for (const row of rows) {
      const cells = row.getChildren().filter($isTableCellNode);
      if (cells.length > 0 && cells.every(cell => cell.getTextContent().trim() === '')) {
        row.remove();
      }
    }

    if (child.getChildrenSize() === 0) {
      child.remove();
    }
  }
}

export function $removeEmptyTableColumns(): void {
  const root = $getRoot();
  for (const child of root.getChildren()) {
    if (!$isTableNode(child)) continue;

    const rows = child.getChildren().filter($isTableRowNode);
    if (rows.length === 0) continue;

    const grid = rows.map(row => row.getChildren().filter($isTableCellNode));
    const columnCount = grid[0]?.length ?? 0;

    for (let col = columnCount - 1; col >= 0; col--) {
      const allEmpty = grid.every(
        cells => col >= cells.length || cells[col].getTextContent().trim() === '',
      );

      if (allEmpty) {
        for (const cells of grid) {
          if (col < cells.length) {
            cells[col].remove();
          }
        }
      }
    }

    if (child.getChildrenSize() === 0 || rows.every(r => r.getChildrenSize() === 0)) {
      child.remove();
    }
  }
}

export function $removeEmptyListItems(root: ElementNode): void {
  const walk = (node: ElementNode) => {
    for (const child of node.getChildren()) {
      if (!$isElementNode(child)) continue;
      walk(child);
      if ($isListItemNode(child) && child.getTextContent().trim() === '') {
        child.remove();
      } else if ($isListNode(child) && child.getChildrenSize() === 0) {
        child.remove();
      }
    }
  };
  walk(root);
}

export function $isPartialColumnSelection(info: {
  tableNodeKey: string;
  startCol: number;
  endCol: number;
}): boolean {
  const tableNode = $getNodeByKey(info.tableNodeKey);
  if (!tableNode || !$isTableNode(tableNode)) return false;
  const totalCols = tableNode.getColumnCount();
  return info.startCol !== 0 || info.endCol < totalCols - 1;
}

function findPipeTableBlocks(lines: string[]): { start: number; end: number }[] {
  const blocks: { start: number; end: number }[] = [];
  let i = 0;
  while (i < lines.length) {
    if (TABLE_ROW_REG_EXP.test(lines[i])) {
      const start = i;
      i++;
      while (
        i < lines.length &&
        (TABLE_ROW_REG_EXP.test(lines[i]) || TABLE_ROW_DIVIDER_REG_EXP.test(lines[i]))
      ) {
        i++;
      }
      blocks.push({ start, end: i - 1 });
    } else {
      i++;
    }
  }
  return blocks;
}

type TableLineMapping = {
  markdown: string;
  lines: string[];
  tableStartLine: number;
  hasHeaderRow: boolean;
  rowToLine: (rowIdx: number) => number;
  rows: TableRowNode[];
};

function $resolveTableLineMapping(
  tableNodeKey: string,
  transformers: Transformer[],
): TableLineMapping | null {
  const tableNode = $getNodeByKey(tableNodeKey);
  if (!tableNode || !$isTableNode(tableNode) || isHtmlOrigin(tableNode)) return null;

  const rows = tableNode.getChildren().filter($isTableRowNode);

  const root = $getRoot();
  let tableOrdinal = -1;
  let count = 0;
  for (const child of root.getChildren()) {
    if ($isTableNode(child) && !isHtmlOrigin(child)) {
      if (child.getKey() === tableNodeKey) {
        tableOrdinal = count;
        break;
      }
      count++;
    }
  }
  if (tableOrdinal === -1) return null;

  const markdown = $getEditorValue(transformers, root);
  const lines = markdown.split('\n');

  const tableBlocks = findPipeTableBlocks(lines);
  if (tableOrdinal >= tableBlocks.length) return null;

  const block = tableBlocks[tableOrdinal];
  const tableStartLine = block.start;

  const firstRow = rows[0];
  const hasHeaderRow = firstRow
    .getChildren()
    .some(cell => $isTableCellNode(cell) && cell.__headerState === TableCellHeaderStates.ROW);

  const rowToLine = (rowIdx: number): number => {
    if (!hasHeaderRow) return tableStartLine + rowIdx;
    return tableStartLine + (rowIdx === 0 ? 0 : rowIdx + 1);
  };

  return { markdown, lines, tableStartLine, hasHeaderRow, rowToLine, rows };
}

export function $getTableSelectionMarkdown(
  tableNodeKey: string,
  startRow: number,
  endRow: number,
  transformers: Transformer[],
): string | null {
  const mapping = $resolveTableLineMapping(tableNodeKey, transformers);
  if (!mapping) return null;

  const { lines, rowToLine, rows } = mapping;
  if (startRow < 0 || endRow >= rows.length || startRow > endRow) return null;

  const firstLine = rowToLine(startRow);
  const lastLine = rowToLine(endRow);
  if (firstLine >= lines.length || lastLine >= lines.length) return null;

  lines[firstLine] = OPENING_TAG + lines[firstLine];
  lines[lastLine] = lines[lastLine] + CLOSING_TAG;

  return lines.join('\n');
}

export function $getTableCellMarkdown(
  tableNodeKey: string,
  row: number,
  col: number,
  transformers: Transformer[],
): string | null {
  const mapping = $resolveTableLineMapping(tableNodeKey, transformers);
  if (!mapping) return null;

  const { lines, rowToLine, rows } = mapping;
  if (row < 0 || row >= rows.length) return null;

  const lineIdx = rowToLine(row);
  if (lineIdx >= lines.length) return null;

  const rowLine = lines[lineIdx];
  const inner = rowLine.slice(rowLine.indexOf('|') + 1, rowLine.lastIndexOf('|'));
  const cells = inner.split('|');
  if (col < 0 || col >= cells.length) return null;

  const rawContent = cells[col].trim();
  const cleanContent = rawContent.replace(/<\/?suggestion[^>]*>/g, '');
  cells[col] = ` ${OPENING_TAG}${cleanContent}${CLOSING_TAG} `;
  lines[lineIdx] = `|${cells.join('|')}|`;

  return lines.join('\n');
}

export function $getTableCellsMarkdown(
  info: TableSelectionInfo,
  transformers: Transformer[],
): string | null {
  const mapping = $resolveTableLineMapping(info.tableNodeKey, transformers);
  if (!mapping) return null;

  const { lines, rows, rowToLine } = mapping;

  if (info.startRow < 0 || info.endRow >= rows.length || info.startRow > info.endRow) return null;

  for (let r = info.startRow; r <= info.endRow; r++) {
    const lineIdx = rowToLine(r);
    if (lineIdx >= lines.length) return null;
    const rowLine = lines[lineIdx];
    const inner = rowLine.slice(rowLine.indexOf('|') + 1, rowLine.lastIndexOf('|'));
    const cells = inner.split('|');

    for (let c = info.startCol; c <= info.endCol; c++) {
      if (c < 0 || c >= cells.length) return null;
      const rawContent = cells[c].trim();
      const cleanContent = rawContent.replace(/<\/?suggestion[^>]*>/g, '');
      const content = cleanContent || '\u200b';
      cells[c] = ` ${OPENING_TAG}${content}${CLOSING_TAG} `;
    }

    lines[lineIdx] = `|${cells.join('|')}|`;
  }

  return lines.join('\n');
}

function $insertTagAtWordBoundary(
  textNode: TextNode,
  offset: number,
  tagNode: TextNode,
  side: 'before' | 'after',
): void {
  const snapped = snapToWordBoundary(
    textNode.getTextContent(),
    offset,
    side === 'before' ? 'left' : 'right',
  );

  const atEdge = side === 'before' ? snapped === 0 : snapped >= textNode.getTextContentSize();

  if (atEdge) {
    if (side === 'before') {
      textNode.insertBefore(tagNode);
    } else {
      textNode.insertAfter(tagNode);
    }
    return;
  }

  const parts = textNode.splitText(snapped);
  if (side === 'before') {
    parts[1].insertBefore(tagNode);
  } else {
    parts[0].insertAfter(tagNode);
  }
}
